import { useEffect, useRef, useState } from "react";
import { useAuth } from "../AuthContext";
import {
	chunkMergeRequests,
	fetchDiscussionBatch,
	runWithConcurrency,
} from "./mergeRequestDiscussions";
import { getReviewerThreads } from "./reviewerThreads";

// A new note bumps updatedAt and a resolve changes the unresolved count, so
// this key goes stale exactly when the classification might change. That lets
// each two-minute poll refetch only the MRs that moved. Bounded with oldest-
// first eviction; cleared on logout.
const summaryCache = new Map();
const MAX_CACHE_ENTRIES = 300;

const cacheKey = (mr) => `${mr.id}:${mr.updatedAt}:${mr.discussions}`;

const cacheSummary = (key, summary) => {
	summaryCache.delete(key);
	summaryCache.set(key, summary);
	while (summaryCache.size > MAX_CACHE_ENTRIES) {
		summaryCache.delete(summaryCache.keys().next().value);
	}
};

export const clearReviewerThreadsCache = () => {
	summaryCache.clear();
};

// MRs with no unresolved threads cannot have a pending thread of the viewer's,
// so skip them rather than paying for a discussions fetch.
const hasUnresolvedThreads = (mr) => mr.discussions > 0;

// Returns a Map of MR id -> getReviewerThreads summary for the MRs on which the
// viewer opened at least one unresolved thread. lastUpdated re-runs the effect
// on every successful poll so batches that failed earlier get retried.
export const useReviewerThreads = (mrs, lastUpdated) => {
	const { user } = useAuth();
	const token = user?.access_token;
	const viewerUsername = user?.profile?.username;
	const [summaries, setSummaries] = useState(() => new Map());
	const summariesRef = useRef(summaries);
	const [refreshKey, setRefreshKey] = useState(0);
	const forceRefreshRef = useRef(false);
	const mrsRef = useRef(mrs);
	mrsRef.current = mrs;
	const signature = mrs.filter(hasUnresolvedThreads).map(cacheKey).join("|");

	// biome-ignore lint/correctness/useExhaustiveDependencies: refreshKey and lastUpdated intentionally trigger reloads.
	useEffect(() => {
		if (!token || !viewerUsername) return;

		const eligible = mrsRef.current.filter(hasUnresolvedThreads);
		const force = forceRefreshRef.current;
		forceRefreshRef.current = false;
		const controller = new AbortController();
		// Until an MR's fresh summary is cached, keep its last one: a new reply
		// bumps updatedAt, and dropping the annotation then would hide exactly
		// the change it reports. The same fallback covers a failed refetch.
		const publish = () => {
			const previous = summariesRef.current;
			const next = new Map();
			for (const mr of eligible) {
				const key = cacheKey(mr);
				const summary = summaryCache.has(key)
					? summaryCache.get(key)
					: previous.get(mr.id);
				if (summary) next.set(mr.id, summary);
			}
			summariesRef.current = next;
			setSummaries(next);
		};

		publish();
		const stale = eligible.filter(
			(mr) => force || !summaryCache.has(cacheKey(mr)),
		);
		if (stale.length === 0) return;

		const load = async () => {
			await runWithConcurrency(
				chunkMergeRequests(stale).map((batch) => async () => {
					try {
						const discussionsByMr = await fetchDiscussionBatch(
							batch,
							token,
							controller.signal,
						);
						for (const [index, mr] of batch.entries()) {
							cacheSummary(
								cacheKey(mr),
								getReviewerThreads(discussionsByMr[index], viewerUsername),
							);
						}
					} catch (error) {
						if (controller.signal.aborted) return;
						// intentional: no loading/error UI. This is a best-effort hint on
						// a list that reports its own fetch errors; a failed batch keeps
						// its last annotation and retries on the next poll.
						console.warn(
							`Skipping reviewer thread status for ${batch.length} MRs: ${error.message}`,
						);
					}
				}),
			);
			if (!controller.signal.aborted) publish();
		};

		load();
		return () => controller.abort();
	}, [lastUpdated, refreshKey, signature, token, viewerUsername]);

	return {
		summaries,
		refetch: () => {
			forceRefreshRef.current = true;
			setRefreshKey((key) => key + 1);
		},
	};
};
